(() => {
  if (window.__awTopicsLoaded) {
    return;
  }

  window.__awTopicsLoaded = true;

  const instances = new Map();
  const sliderUtils = window.AWSliderCoreUtils;

  const selectors = {
    root: "[data-aw-topics]",
    viewport: "[data-aw-topics-viewport]",
    list: "[data-aw-topics-list]",
    item: "[data-aw-topics-item]",
    scrollbar: "[data-aw-topics-scrollbar]",
    thumb: "[data-aw-topics-scrollbar-thumb]",
    prev: "[data-aw-topics-prev]",
    next: "[data-aw-topics-next]",
  };

  class AWTopics {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || "";
      this.viewport = root.querySelector(selectors.viewport);
      this.list = root.querySelector(selectors.list);
      this.items = Array.from(root.querySelectorAll(selectors.item));
      this.scrollbar = root.querySelector(selectors.scrollbar);
      this.thumb = root.querySelector(selectors.thumb);
      this.prevButton = root.querySelector(selectors.prev);
      this.nextButton = root.querySelector(selectors.next);
      this.rafId = null;
      this.teardownResizeObserver = null;

      this.onScroll = this.requestUpdate.bind(this);
      this.onPrevClick = this.scrollPrev.bind(this);
      this.onNextClick = this.scrollNext.bind(this);
      this.onResize = this.update.bind(this);
      this.onImageLoad = this.requestUpdate.bind(this);

      this.bindEvents();
      this.update();
    }

    bindEvents() {
      if (this.viewport) {
        this.viewport.addEventListener("scroll", this.onScroll, { passive: true });
      }

      if (this.prevButton) {
        this.prevButton.addEventListener("click", this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener("click", this.onNextClick);
      }

      this.teardownResizeObserver = sliderUtils.observeResize([this.root, this.list], this.onResize);
      sliderUtils.bindImageLoad(this.root, this.onImageLoad);
    }

    getMaxScroll() {
      return sliderUtils.getMaxScroll(this.viewport);
    }

    getScrollAmount() {
      return sliderUtils.getItemDistance({
        list: this.list,
        items: this.items,
        itemSelector: selectors.item,
        viewport: this.viewport,
      });
    }

    getScrollBehavior() {
      return sliderUtils.getScrollBehavior();
    }

    scrollByAmount(amount) {
      if (!this.viewport) {
        return;
      }

      this.viewport.scrollBy({
        left: amount,
        behavior: this.getScrollBehavior(),
      });
    }

    scrollPrev() {
      if (this.prevButton && this.prevButton.getAttribute("aria-disabled") === "true") {
        return;
      }

      this.scrollByAmount(this.getScrollAmount() * -2);
    }

    scrollNext() {
      if (this.nextButton && this.nextButton.getAttribute("aria-disabled") === "true") {
        return;
      }

      this.scrollByAmount(this.getScrollAmount() * 2);
    }

    requestUpdate() {
      if (this.rafId) {
        return;
      }

      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    }

    update() {
      if (!this.viewport) {
        return;
      }

      const maxScroll = this.getMaxScroll();
      const canScroll = maxScroll > 1;
      const scrollLeft = Math.max(0, this.viewport.scrollLeft);
      const atStart = scrollLeft <= 1;
      const atEnd = scrollLeft >= maxScroll - 1;

      sliderUtils.updateButtonState(this.prevButton, !canScroll || atStart);
      sliderUtils.updateButtonState(this.nextButton, !canScroll || atEnd);

      if (this.scrollbar) {
        this.scrollbar.classList.toggle("is-hidden", !canScroll);
      }

      sliderUtils.updateScrollbar({
        viewport: this.viewport,
        scrollbar: this.scrollbar,
        thumb: this.thumb,
        enabled: canScroll,
      });
    }

    selectBlock(blockId, fallbackTarget) {
      if (!this.viewport) {
        return;
      }

      sliderUtils.selectBlockItem({
        root: this.root,
        viewport: this.viewport,
        itemSelector: selectors.item,
        blockId,
        fallbackTarget,
      });
      this.requestUpdate();
    }

    destroy() {
      sliderUtils.cancelFrame(this, "rafId");

      if (this.viewport) {
        this.viewport.removeEventListener("scroll", this.onScroll);
      }

      if (this.prevButton) {
        this.prevButton.removeEventListener("click", this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener("click", this.onNextClick);
      }

      if (this.teardownResizeObserver) {
        this.teardownResizeObserver();
        this.teardownResizeObserver = null;
      }

      sliderUtils.unbindImageLoad(this.root, this.onImageLoad);
    }
  }

  function getRoot(target) {
    if (!target) {
      return null;
    }

    if (typeof target.matches === "function" && target.matches(selectors.root)) {
      return target;
    }

    if (typeof target.querySelector === "function") {
      const root = target.querySelector(selectors.root);

      if (root) {
        return root;
      }
    }

    if (typeof target.closest === "function") {
      return target.closest(selectors.root);
    }

    return null;
  }

  function destroySection(sectionId) {
    const instance = instances.get(sectionId);

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(sectionId);
  }

  function initSection(root) {
    if (!root) {
      return;
    }

    const sectionId = root.dataset.sectionId;

    if (!sectionId) {
      return;
    }

    destroySection(sectionId);
    instances.set(sectionId, new AWTopics(root));
  }

  function initAll(container = document) {
    container.querySelectorAll(selectors.root).forEach((root) => {
      initSection(root);
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    initSection(getRoot(event.target));
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = getRoot(event.target);

    if (!root) {
      return;
    }

    destroySection(root.dataset.sectionId);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    const blockId = event.detail && event.detail.blockId;
    const instance = sectionId ? instances.get(sectionId) : null;

    if (!instance) {
      return;
    }

    instance.selectBlock(blockId, event.target);
  });

  document.addEventListener("shopify:block:deselect", (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    const instance = sectionId ? instances.get(sectionId) : null;

    if (instance) {
      instance.update();
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
